import { Box, Button, Container, Grid2, List, ListItem, ListItemText, Paper, Stack, Typography, useTheme } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import CarouselGallery from "../components/Common/CarouselGallery";
import ErrorMessage from "../components/Common/ErrorMessage";
import MarkRecipeIcon from "../components/Common/Icons/MarkRecipeIcon";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import PageTitle from "../components/Common/PageTitle";
import TagList from "../components/Common/TagList";
import GroupedIngredientsSection from "../components/Recipes/GroupedIngredientsSection";
import RecipeDetails from "../components/Recipes/RecipeDetails"; // Import the new component
import { RecipeContext } from "../contexts/RecipeContext";
import { UserContext } from "../contexts/UserContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { secondaryButton } from "../styles/commonStyles";
import { recipePageStyles } from "../styles/recipeStyles";

const SECTION_GAP = 4;

const RecipePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = recipePageStyles(theme);
  const { id } = useParams();
  const { recipes, loading, error } = useContext(RecipeContext);
  const { user } = useContext(UserContext);
  const isAuthenticated = !!user;
  const recipe = recipes.find((r) => Number(r.id) === Number(id));

  useDocumentTitle(recipe?.title || "");

  useEffect(() => {
    if (recipe) {
      document.title = `${recipe.title} - ${t("appTitle")}`;
    }
  }, [recipe, t]);

  if (loading) return <LoadingSpinner />;
  if (error || !recipe) return <ErrorMessage error={error || t("recipeNotFound")} />;

  return (
    <Container>
      {/* Recipe Title & Actions */}
      <Box sx={styles.titleBox}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PageTitle text={recipe.title} />
          <MarkRecipeIcon recipeId={recipe.id} />
        </Box>
        <Box>
          {isAuthenticated && (
            <Box component={Link} to={`/recipe/edit/${recipe.id}`} sx={{ textDecoration: "none" }}>
              <Button variant="contained" sx={secondaryButton(theme)}>
                {t("editRecipe")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Recipe Image Carousel */}
      <CarouselGallery images={recipe.images || ["/assets/images/Placeholder.webp"]} />

      {/* Recipe Description */}
      <Box sx={{ mt: SECTION_GAP }}>
        <Typography variant="body1" gutterBottom>
          <strong>{recipe.description}</strong>
        </Typography>
      </Box>

      {/* Recipe Details */}
      <Box sx={{ mt: SECTION_GAP }}>
        {/* <Typography variant="h6" component="h3" gutterBottom>
          {t("recipeDetails")}
        </Typography> */}
        <RecipeDetails recipe={recipe} />
      </Box>

      {/* Ingredients Section */}
      {recipe.ingredients && recipe.ingredients.length > 0 && (
        <Box sx={{ mt: SECTION_GAP }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {t("ingredients")}
          </Typography>
          <Paper elevation={3} sx={styles.subtleBackground}>
            <GroupedIngredientsSection ingredients={recipe.ingredients} />
          </Paper>
        </Box>
      )}

      {/* Instructions Section */}
      <Box sx={{ mt: SECTION_GAP }}>
        <Typography variant="h6" component="h3" gutterBottom>
          {t("instructions")}
        </Typography>
        <Paper elevation={3} sx={styles.subtleBackground}>
          <List>
            {recipe.instructions &&
              recipe.instructions.map((instruction, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`${index + 1}. ${instruction.description}`} />
                </ListItem>
              ))}
          </List>
        </Paper>
      </Box>

      {/* Tags and Images */}
      <Box sx={{ mt: SECTION_GAP }}>
        <Stack spacing={2}>
          {/* Recipe Tags */}
          <TagList tags={recipe.tags} />

          {/* Recipe Images */}
          {recipe.images?.length > 0 && (
            <>
              <Typography variant="h6" component="h3" gutterBottom>
                {t("images")}
              </Typography>
              <Grid2 container spacing={2}>
                {recipe.images.map((image, index) => (
                  <Grid2
                    key={index}
                    grid={{
                      xs: 12,
                      sm: 6,
                    }}
                  >
                    <Box component="img" src={image} alt={`Recipe ${index + 1}`} sx={styles.imageStyle} />
                  </Grid2>
                ))}
              </Grid2>
            </>
          )}
        </Stack>
      </Box>

      {/* Author Information */}
      <Box sx={{ mt: SECTION_GAP, mb: 4 }}>
        {recipe.author && (
          <Typography variant="subtitle2" color="text.secondary">
            {`${t("author")} ${recipe.author}`}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default RecipePage;
